import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Button from './Button';
import { heroSlideService } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const HeroContainer = styled.section`
  position: relative;
  width: 100vw;
  height: auto;
  box-sizing: border-box;
  margin-left: calc(-50vw + 50%);
  overflow: hidden;
  margin-top: 0;
  padding-top: 0;
  margin-bottom: 0;
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
`;

const SliderContainer = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  transform: translateX(-${props => props.currentSlide * 100}%);
  transition: ${props => props.isTransitioning ? 'transform 0.5s ease-in-out' : 'none'};
`;

const Slide = styled.div`
  min-width: 100%;
  height: auto;
  background: none;
  display: block;
  position: relative;
  margin: 0;
  padding: 0;
  line-height: 0;
`;

const MobileSlideImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
  aspect-ratio: 16 / 9;
  vertical-align: bottom;
  margin: 0;
  padding: 0;
  
  /* Reduced height on desktop */
  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    height: 87vh;
    
    aspect-ratio: auto;
  }
`;

const SlideContent = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.md};
  }
`;

const TextContent = styled.div`
  max-width: 600px;
  width: 100%;
  color: white;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    max-width: 100%;
    text-align: center;
    padding: 0 ${props => props.theme.spacing.md};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 ${props => props.theme.spacing.sm};
  }
`;

const MainHeading = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${props => props.theme.fontWeights.bold};
  line-height: 1.2;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: clamp(2rem, 4vw, 3rem);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: clamp(1.8rem, 4vw, 2.5rem);
    text-align: center;
  }
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.xl};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing['3xl']};
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${props => props.theme.fontSizes.lg};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.theme.fontSizes.base};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
  }
`;

const CTAButton = styled(Button)`
  background: ${props => props.theme.colors.gradient.primary};
  color: white;
  border: none;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing['2xl']};
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  box-shadow: 0 4px 20px rgba(255, 68, 68, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(255, 68, 68, 0.4);
    background: ${props => props.theme.colors.gradient.secondary};
  }
  
  svg {
    margin-left: ${props => props.theme.spacing.sm};
  }
`;

const NavArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 3;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: ${props => props.theme.spacing.xl};
  }
  
  &.next {
    right: ${props => props.theme.spacing.xl};
  }
  
  svg {
    font-size: ${props => props.theme.fontSizes.lg};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 40px;
    height: 40px;
    
    &.prev {
      left: ${props => props.theme.spacing.md};
    }
    
    &.next {
      right: ${props => props.theme.spacing.md};
    }
  }
`;

const SlideIndicators = styled.div`
  position: absolute;
  bottom: ${props => props.theme.spacing['3xl']};
  left: ${props => props.theme.spacing['4xl']};
  display: flex;
  gap: ${props => props.theme.spacing.md};
  z-index: 3;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    left: 50%;
    transform: translateX(-50%);
    bottom: ${props => props.theme.spacing['2xl']};
  }
`;

const Indicator = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.6);
  background: ${props => props.active ? 'white' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: white;
    background: white;
    transform: scale(1.2);
  }
`;

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(1); // Start at 1 (first real slide)
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fallback slides if API fails or no slides in database
  const defaultSlides = [
    {
      _id: '1',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      title: 'Empowering Your IT Career',
      subtitle: 'Explore our courses and take your skills to the next level.',
      buttonText: 'Get Started',
      buttonLink: '/courses'
    },
    {
      _id: '2',
      image: 'https://images.unsplash.com/photo-1519904981063-b0cf448d479e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      title: 'Master Modern Technologies',
      subtitle: 'Learn cutting-edge skills with industry experts and hands-on projects.',
      buttonText: 'Get Started',
      buttonLink: '/courses'
    },
    {
      _id: '3',
      image: 'https://images.unsplash.com/photo-1464822759844-d150baec0494?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      title: 'Build Your Future Today',
      subtitle: 'Join thousands of successful graduates who transformed their careers.',
      buttonText: 'Get Started',
      buttonLink: '/courses'
    },
    {
      _id: '4',
      image: 'https://images.unsplash.com/photo-1506197603052-3cc9c3a201bd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      title: 'Transform Your Career',
      subtitle: 'Get industry-ready skills and 100% placement assistance.',
      buttonText: 'Get Started',
      buttonLink: '/courses'
    }
  ];

  // Fetch hero slides from API
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const activeSlides = await heroSlideService.getPublicSlides();
        setSlides(activeSlides && activeSlides.length ? activeSlides : defaultSlides);
      } catch (error) {
        console.error('Failed to fetch hero slides:', error);
        setSlides(defaultSlides);
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
    // eslint-disable-next-line
  }, []);

  // Create infinite loop: [last, slide1, slide2, slide3, slide4, first]
  const infiniteSlides = [
    slides[slides.length - 1], // Last slide clone at beginning
    ...slides,
    slides[0] // First slide clone at end
  ];

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide(prev => {
      const next = prev + 1;
      
      // If we reach the cloned first slide (last index)
      if (next === infiniteSlides.length - 1) {
        setTimeout(() => {
          setIsTransitioning(false);
          setCurrentSlide(1); // Jump to real first slide without animation
        }, 500);
      } else {
        setTimeout(() => setIsTransitioning(false), 500);
      }
      
      return next;
    });
  }, [isTransitioning, infiniteSlides.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [nextSlide]);

  const prevSlide = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide(prev => {
      const next = prev - 1;
      
      // If we reach the cloned last slide (first index)
      if (next === 0) {
        setTimeout(() => {
          setIsTransitioning(false);
          setCurrentSlide(slides.length); // Jump to real last slide without animation
        }, 500);
      } else {
        setTimeout(() => setIsTransitioning(false), 500);
      }
      
      return next;
    });
  };

  const goToSlide = (index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index + 1); // +1 because of the cloned slide at beginning
    setTimeout(() => setIsTransitioning(false), 500);
  };

  // Show loading spinner while fetching
  if (loading) {
    return (
      <HeroContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <LoadingSpinner />
        </div>
      </HeroContainer>
    );
  }

  return (
    <HeroContainer>
      <SliderWrapper>
        <SliderContainer currentSlide={currentSlide} isTransitioning={isTransitioning}>
          {infiniteSlides.map((slide, index) => (
            <Slide key={`${slide._id}-${index}`} bgImage={slide.image} $showText={slide.showText}>
              <MobileSlideImage src={slide.image} alt={slide.title} />
              <SlideContent>
                {slide.showText !== false && (
                  <TextContent>
                    <MainHeading>
                      {slide.title}
                    </MainHeading>
                    
                    <Subtitle>
                      {slide.subtitle}
                    </Subtitle>
                    
                    <ButtonGroup>
                      <CTAButton
                        as={Link}
                        to={slide.buttonLink || '/courses'}
                        size="large"
                      >
                        {slide.buttonText || 'Get Started'}
                        <FiArrowRight />
                      </CTAButton>
                    </ButtonGroup>
                  </TextContent>
                )}
              </SlideContent>
            </Slide>
          ))}
        </SliderContainer>
        
        <NavArrow className="prev" onClick={prevSlide}>
          <FiChevronLeft />
        </NavArrow>
        
        <NavArrow className="next" onClick={nextSlide}>
          <FiChevronRight />
        </NavArrow>
        
        <SlideIndicators>
          {slides.map((_, index) => (
            <Indicator
              key={index}
              active={index === currentSlide - 1} // -1 because currentSlide accounts for cloned slide
              onClick={() => goToSlide(index)}
            />
          ))}
        </SlideIndicators>
      </SliderWrapper>
    </HeroContainer>
  );
};

export default HeroSection;
